import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Badge,
  Box,
  CssBaseline,
  IconButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Divider,
  Switch,
  TextField,
  Autocomplete,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Recycling as RecyclingIcon,
  Assignment as OrdersIcon,
  Add as AddIcon,
  Category as CategoryIcon,
  Inventory2 as ContainerIcon,
  Place as PlaceIcon,
  DirectionsCar as TrucksIcon,
  Person as DriverIcon,
  HelpOutline as HelpOutlineIcon,
  Notifications as NotificationsIcon,
  Language as LanguageIcon,
  Check as CheckIcon,
  ChevronRight as ChevronRightIcon,
  Storefront as SuppliersIcon,
  Inventory2Outlined as OrdersMonitoringIcon,
  AssignmentIndOutlined as DriverRequestsIcon,
  RouteOutlined as RoutesIcon,
  LabelOutlined as ZoneLabelsIcon,
  WarehouseOutlined as FacilitiesIcon,
  TourOutlined as TripsIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../stores/auth.store';
import { useOrganizationStore } from '../stores/organization.store';
import { useNotificationStore } from '../stores/notification.store';
import { OrganizationSchema } from '../types/organization.type';
import { useAuthPermissions } from '../hooks/useAuthPermissions';
import { OPS_NAV_CATALOG } from '../auth';
import oneSignalService from '../services/onesignal.service';
import OrgLogo from './organizations/OrgLogo';
import { Sidebar } from './Sidebar';
import { SelfSetupGate } from './selfSetup/SelfSetupGate';
import {
  SelfSetupNavItem,
  SelfSetupSidebarEffect,
} from './selfSetup/SelfSetupNavItem';
import { SelfSetupTourProvider } from './selfSetup/SelfSetupTourProvider';
import { APP_LANGUAGES, setAppLanguage, type AppLanguage } from '../i18n/app';
import { APP_FONT_FAMILY } from '../constants/fonts';
import { APP_SHAPE } from '../constants/shape';
import { isOpsPilotPathAllowed } from '../auth/pilot';
import PilotLockedView from './pilot/PilotLockedView';

const drawerWidth = 280;
const collapsedDrawerWidth = 76;
const SIDEBAR_COLLAPSED_STORAGE_KEY = 'ops.sidebarCollapsed';

// Language endonyms use the same unified multi-script web-font stack as the rest
// of the app so every script renders consistently regardless of the OS.
const LANGUAGE_FONT_FAMILY = APP_FONT_FAMILY;

// Maps the catalog's English `text` to an i18n key (falls back to the raw text).
const NAV_LABEL_KEYS: Record<string, string> = {
  Suppliers: 'suppliers',
  'All Suppliers': 'allSuppliers',
  'Collection Points': 'supplierCollectionPoints',
  'Labels': 'zoneLabels',
  'Orders Monitoring': 'ordersMonitoring',
  Trips: 'trips',
  'Order Approvals': 'orderApprovals',
  'Driver Requests': 'driverRequests',
  'Pickup Orders': 'pickupOrders',
  'All Pickup Orders': 'allPickupOrders',
  'Create Pickup Order': 'createPickupOrder',
  Trucks: 'trucks',
  Drivers: 'drivers',
  Routes: 'routes',
  Facilities: 'facilities',
  Fleet: 'fleet',
  'Route Planning': 'routePlanning',
  Monitoring: 'monitoring',
  Recycling: 'recycling',
  'Material Types': 'materialTypes',
  Containers: 'containers',
  'Container List': 'containerList',
  'Collection Sites': 'collectionSites',
};

// Per-route icons keep the sidebar visually consistent with the page headers.
const NAV_ICONS: Record<string, React.ReactNode> = {
  '/suppliers': <SuppliersIcon />,
  '/suppliers/collection-points': <PlaceIcon />,
  '/suppliers/zones': <ZoneLabelsIcon />,
  '/monitoring/orders': <OrdersMonitoringIcon />,
  '/monitoring/driver-requests': <DriverRequestsIcon />,
  '/monitoring/trips': <TripsIcon />,
  '/monitoring/approvals': <OrdersMonitoringIcon />,
  '/pickup_orders': <OrdersIcon />,
  '/pickup_orders/create': <AddIcon />,
  '/transportation/fleet': <TrucksIcon />,
  '/transportation/drivers': <DriverIcon />,
  '/transportation/routes': <RoutesIcon />,
  '/transportation/facilities': <FacilitiesIcon />,
  '/recycling/materials': <CategoryIcon />,
  '/containers/list': <ContainerIcon />,
  '/containers/sites': <PlaceIcon />,
};

interface LayoutProps {
  children: React.ReactNode;
  onThemeToggle: () => void;
  /** Skips the default content padding/margins/rounded corners so the page can use the full viewport (map-heavy pages). */
  fullBleed?: boolean;
}

const Layout: React.FC<LayoutProps> = ({ children, onThemeToggle, fullBleed = false }) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true',
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState<null | HTMLElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { unreadCount } = useNotificationStore();

  const effectiveDrawerWidth = sidebarCollapsed ? collapsedDrawerWidth : drawerWidth;

  const navigate = useNavigate();
  const location = useLocation();
  const {
    logout,
    user,
    accessibleOrgIds,
    isPlatformAdmin: platformAdmin,
    tokenPayload,
  } = useAuthStore();
  const isPilot = tokenPayload?.is_pilot === true;
  const { organizations, getOrganizations, selectedOrganization, setSelectedOrganization } =
    useOrganizationStore();
  const { filterOpsNavItems } = useAuthPermissions();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('sm'));

  const contentTransition = theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  });

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const handleSidebarCollapseToggle = useCallback(
    () => setSidebarCollapsed((prev) => !prev),
    [],
  );

  const navLabel = (text: string) => t(`nav.${NAV_LABEL_KEYS[text] ?? text}`, text);
  const navIconFor = (path: string) => NAV_ICONS[path] ?? <OrdersIcon />;

  const orgOptions = useMemo(() => {
    if (platformAdmin || accessibleOrgIds.length === 0) {
      return organizations;
    }
    const allowed = new Set(accessibleOrgIds);
    return organizations.filter((o) => allowed.has(o.id));
  }, [organizations, accessibleOrgIds, platformAdmin]);

  useEffect(() => {
    const savedOrg = sessionStorage.getItem('selectedOrganization');
    if (savedOrg) {
      try {
        const parsedOrg = JSON.parse(savedOrg) as OrganizationSchema;
        if (
          platformAdmin ||
          accessibleOrgIds.length === 0 ||
          accessibleOrgIds.includes(parsedOrg.id)
        ) {
          setSelectedOrganization(parsedOrg);
        } else {
          sessionStorage.removeItem('selectedOrganization');
        }
      } catch (error) {
        console.error('Error parsing saved organization:', error);
      }
    }
  }, [accessibleOrgIds, platformAdmin, setSelectedOrganization]);

  useEffect(() => {
    if (!isPilot || selectedOrganization || orgOptions.length === 0) return;
    const firstOrganization = orgOptions[0];
    setSelectedOrganization(firstOrganization);
    sessionStorage.setItem('selectedOrganization', JSON.stringify(firstOrganization));
  }, [isPilot, selectedOrganization, orgOptions, setSelectedOrganization]);

  const fetchOrganizations = useCallback(
    async (page = 1, input = '') => {
      const params = { page, limit: 10, name: input || undefined };
      await getOrganizations(params);
    },
    [getOrganizations],
  );

  useEffect(() => {
    fetchOrganizations(1, searchQuery);
  }, [fetchOrganizations, searchQuery]);

  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => setAnchorEl(null);

  const handleLanguageMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLanguageMenuAnchor(event.currentTarget);
  };

  const handleLanguageMenuClose = () => setLanguageMenuAnchor(null);

  const handleLanguageSelect = (language: AppLanguage) => {
    setAppLanguage(language);
    setLanguageMenuAnchor(null);
    setAnchorEl(null);
  };

  const handleOrganizationChange = async (
    _: unknown,
    newValue: OrganizationSchema | null,
  ) => {
    if (newValue) {
      setSelectedOrganization(newValue);
      sessionStorage.setItem('selectedOrganization', JSON.stringify(newValue));

      if (!isPilot && user && !user.data?.onesignal_player_id) {
        try {
          if (oneSignalService.isReady()) {
            await oneSignalService.registerPlayerWithBackend(newValue.id, user.sub);
          }
        } catch (error) {
          console.error('Error registering OneSignal player for new organization:', error);
        }
      }

      window.location.reload();
    }
  };

  useEffect(() => {
    if (isPilot) return;

    const registerOneSignalForOrganization = async () => {
      if (selectedOrganization && user) {
        if (user.data?.onesignal_player_id) {
          return;
        }
        try {
          if (oneSignalService.isReady()) {
            await oneSignalService.registerPlayerWithBackend(
              selectedOrganization.id,
              user.sub,
            );
          }
        } catch (error) {
          console.error('Error registering OneSignal player:', error);
        }
      }
    };

    const timer = setTimeout(registerOneSignalForOrganization, 2000);
    return () => clearTimeout(timer);
  }, [selectedOrganization, user, isPilot]);

  const visibleMenuItems = useMemo(() => {
    if (!selectedOrganization) {
      return [];
    }
    if (isPilot) {
      const orgType = selectedOrganization.organization_type;
      return OPS_NAV_CATALOG.filter((item) => item.allowedTypes.includes(orgType))
        .map((item) => {
          if (!item.subItems) return item;
          const subItems = item.subItems.filter(
            (subItem) => !subItem.allowedTypes || subItem.allowedTypes.includes(orgType),
          );
          return subItems.length > 0 ? { ...item, subItems } : null;
        })
        .filter((item): item is (typeof OPS_NAV_CATALOG)[number] => item !== null);
    }
    return filterOpsNavItems(OPS_NAV_CATALOG, selectedOrganization.organization_type);
  }, [selectedOrganization, filterOpsNavItems, isPilot]);

  const logoSrc = new URL(
    theme.palette.mode === 'dark' ? '/images/ops_dark.png' : '/images/ops_light.png',
    import.meta.url,
  ).href;

  const orgData = selectedOrganization?.data as Record<string, unknown> | undefined;
  const orgLogoUrl =
    (typeof orgData?.logo_url === 'string' && orgData.logo_url) ||
    (typeof orgData?.logo === 'string' && orgData.logo) ||
    null;

  const renderNavItem = (text: string, path: string) => (
    <SelfSetupNavItem
      key={path}
      item={{
        path,
        text,
        icon: navIconFor(path),
        label: navLabel(text),
        selected: location.pathname === path,
        locked: isPilot && path !== '/pickup_orders' && path !== '/pickup_orders/create',
      }}
      onNavigate={(nextPath) => navigate(nextPath)}
    />
  );

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Sidebar.Header
        collapsedChildren={
          <Box
            aria-label={t('common.appName')}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {/* Clip strictly to the logo's circular mark (the ring around "RE")
                  so the following letters never bleed in as an edge sliver. */}
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  flexShrink: 0,
                  overflow: 'hidden',
                }}
              >
                <Box
                  component="img"
                  src={logoSrc}
                  alt={t('common.appName')}
                  sx={{
                    height: '28.75px',
                    width: 'auto',
                    maxWidth: 'none',
                    display: 'block',
                    ml: '-0.41px',
                    mt: '-0.13px',
                  }}
                />
              </Box>
            </Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                fontSize: '0.66rem',
                letterSpacing: '0.06em',
                lineHeight: 1,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Ops
            </Typography>
          </Box>
        }
      >
        <img
          src={logoSrc}
          alt={t('common.appName')}
          style={{ height: 44, maxWidth: '100%', objectFit: 'contain' }}
        />
      </Sidebar.Header>

      <Sidebar.ExpandedOnly>
        <Divider />
        <Box sx={{ px: 2, py: 2 }}>
          <Autocomplete
            value={selectedOrganization}
            onChange={handleOrganizationChange}
            onInputChange={(_, newInputValue) => setSearchQuery(newInputValue)}
            options={orgOptions}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('common.organization')}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: `${theme.palette.primary.main}40` },
                    '&:hover fieldset': { borderColor: `${theme.palette.primary.main}80` },
                    '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main },
                  },
                  '& .MuiInputLabel-root': { color: `${theme.palette.primary.main}80` },
                  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                }}
              />
            )}
            fullWidth
          />
        </Box>
        <Divider />
      </Sidebar.ExpandedOnly>

      <Sidebar.Nav>
        {visibleMenuItems.map((item) =>
          item.subItems && item.subItems.length > 0 ? (
            <Sidebar.NavGroup key={item.text} label={navLabel(item.text)}>
              {item.subItems.map((subItem) => renderNavItem(subItem.text, subItem.path))}
            </Sidebar.NavGroup>
          ) : (
            renderNavItem(item.text, item.path)
          ),
        )}

        {selectedOrganization && visibleMenuItems.length === 0 && (
          <Sidebar.ExpandedOnly>
            <Typography
              variant="caption"
              sx={{ p: 2, display: 'block', textAlign: 'center', color: 'text.secondary' }}
            >
              {t('nav.noModules')}
            </Typography>
          </Sidebar.ExpandedOnly>
        )}
      </Sidebar.Nav>

      <Box sx={{ flexGrow: 1 }} />

      <Sidebar.ExpandedOnly>
        <Divider />
      </Sidebar.ExpandedOnly>
      <Sidebar.Nav>
        {selectedOrganization && (
          <Sidebar.NavItem
            icon={
              <Badge color="error" badgeContent={unreadCount} overlap="circular">
                <NotificationsIcon />
              </Badge>
            }
            label={t('nav.notifications')}
            selected={location.pathname === '/notifications'}
            onClick={() => navigate('/notifications')}
            locked={isPilot}
          />
        )}
        <Sidebar.NavItem
          icon={<HelpOutlineIcon />}
          label={t('nav.help')}
          selected={location.pathname.startsWith('/knowledge-base')}
          onClick={() => navigate('/knowledge-base')}
        />
        <Sidebar.NavItem
          icon={
            <OrgLogo
              name={selectedOrganization?.name ?? user?.username ?? t('account.title')}
              logoUrl={orgLogoUrl}
              size={28}
            />
          }
          label={user?.username ?? t('account.title')}
          selected={Boolean(anchorEl)}
          onClick={handleProfileMenuOpen}
        />
      </Sidebar.Nav>

      <Sidebar.CollapseToggle
        onClick={handleSidebarCollapseToggle}
        expandLabel={t('nav.expandMenu')}
        collapseLabel={t('nav.collapseMenu')}
      />
    </Box>
  );

  return (
    <SelfSetupTourProvider>
      <SelfSetupSidebarEffect
        onExpandDesktop={() => setSidebarCollapsed(false)}
        onOpenMobile={() => {
          if (isMobileViewport) setMobileOpen(true);
        }}
      />
      <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          display: { xs: 'block', sm: 'none' },
          width: { sm: `calc(100% - ${effectiveDrawerWidth}px)` },
          ml: { sm: `${effectiveDrawerWidth}px` },
          bgcolor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: 'none',
          transition: { sm: contentTransition },
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <IconButton
            color="inherit"
            aria-label={t('nav.openMenu')}
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' }, color: theme.palette.primary.main }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />
        </Toolbar>
      </AppBar>

      <Sidebar
        width={drawerWidth}
        collapsedWidth={collapsedDrawerWidth}
        collapsed={sidebarCollapsed}
        mobileOpen={mobileOpen}
        onMobileClose={handleDrawerToggle}
      >
        {drawer}
      </Sidebar>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${effectiveDrawerWidth}px)` },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: theme.palette.background.paper,
          transition: { sm: contentTransition },
        }}
      >
        <Toolbar sx={{ display: { xs: 'flex', sm: 'none' } }} />
        <Box
          sx={fullBleed ? {
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            bgcolor: theme.palette.background.default,
          } : {
            flexGrow: 1,
            p: 3,
            bgcolor: theme.palette.background.default,
            borderRadius: `${APP_SHAPE.radiusLg}px`,
            mt: { xs: 1.5, sm: 2 },
            mr: { xs: 1.5, sm: 2 },
            mb: { xs: 1.5, sm: 2 },
            ml: { xs: 1.5, sm: 0 },
          }}
        >
          {!isPilot && <SelfSetupGate />}
          {isPilot && !isOpsPilotPathAllowed(location.pathname) ? (
            <PilotLockedView />
          ) : (
            children
          )}
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.paper,
            boxShadow: theme.shadows[3],
            borderRadius: '12px',
            minWidth: 220,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <OrgLogo
            name={selectedOrganization?.name ?? user?.username ?? 'Account'}
            logoUrl={orgLogoUrl}
            size={36}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
              {user?.username}
            </Typography>
            {selectedOrganization && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {selectedOrganization.name}
              </Typography>
            )}
          </Box>
        </Box>
        <Divider />
        <MenuItem
          onClick={onThemeToggle}
          sx={{
            transition: 'all 0.2s ease-in-out',
            '&:hover': { bgcolor: `${theme.palette.primary.main}10` },
          }}
        >
          <ListItemIcon>
            {theme.palette.mode === 'dark' ? (
              <DarkModeIcon fontSize="small" />
            ) : (
              <LightModeIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText primary={t('account.darkMode')} />
          <Switch
            edge="end"
            size="small"
            checked={theme.palette.mode === 'dark'}
            onClick={(event) => event.stopPropagation()}
            onChange={onThemeToggle}
          />
        </MenuItem>
        <MenuItem
          onClick={handleLanguageMenuOpen}
          sx={{
            transition: 'all 0.2s ease-in-out',
            '&:hover': { bgcolor: `${theme.palette.primary.main}10` },
          }}
        >
          <ListItemIcon>
            <LanguageIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('account.languages')} />
          <ChevronRightIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={logout}
          sx={{
            transition: 'all 0.2s ease-in-out',
            '&:hover': { bgcolor: `${theme.palette.error.main}10` },
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" sx={{ color: theme.palette.error.main }} />
          </ListItemIcon>
          {t('account.logout')}
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={languageMenuAnchor}
        open={Boolean(languageMenuAnchor)}
        onClose={handleLanguageMenuClose}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.paper,
            boxShadow: theme.shadows[3],
            borderRadius: '12px',
            minWidth: 240,
            maxHeight: 420,
            py: 0.5,
          },
        }}
        MenuListProps={{ dense: true }}
      >
        <Typography
          variant="overline"
          sx={{
            display: 'block',
            px: 2,
            py: 0.5,
            color: 'text.secondary',
            fontWeight: 700,
            letterSpacing: '0.08em',
          }}
        >
          {t('account.languages')}
        </Typography>
        <Divider sx={{ mb: 0.5 }} />
        {APP_LANGUAGES.map((language) => {
          const isActive = i18n.language.startsWith(language.code);
          const localizedName = t(
            `languages.${language.englishLabel.toLowerCase()}`,
            language.englishLabel,
          );
          const showSecondary = localizedName !== language.nativeLabel;
          return (
            <MenuItem
              key={language.code}
              selected={isActive}
              onClick={() => handleLanguageSelect(language.code)}
              dir="ltr"
              sx={{
                gap: 1.25,
                minHeight: 48,
                py: 0.75,
                px: 1.25,
                mx: 0.75,
                borderRadius: '10px',
                transition: 'all 0.2s ease-in-out',
                '&:hover': { bgcolor: `${theme.palette.primary.main}10` },
                '&.Mui-selected': {
                  bgcolor: `${theme.palette.primary.main}14`,
                  '&:hover': { bgcolor: `${theme.palette.primary.main}1F` },
                },
              }}
            >
              <Box
                aria-hidden
                sx={{
                  width: 30,
                  height: 30,
                  flexShrink: 0,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  bgcolor: isActive ? theme.palette.primary.main : theme.palette.action.hover,
                  color: isActive
                    ? theme.palette.primary.contrastText
                    : theme.palette.text.secondary,
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                {language.code.toUpperCase()}
              </Box>
              <ListItemText
                primary={language.nativeLabel}
                secondary={showSecondary ? localizedName : undefined}
                primaryTypographyProps={{
                  sx: {
                    fontFamily: LANGUAGE_FONT_FAMILY,
                    fontSize: '0.95rem',
                    fontWeight: isActive ? 700 : 500,
                    lineHeight: 1.35,
                    textAlign: 'start',
                  },
                  dir: 'ltr',
                }}
                secondaryTypographyProps={{
                  sx: {
                    fontFamily: LANGUAGE_FONT_FAMILY,
                    fontSize: '0.72rem',
                    lineHeight: 1.3,
                    textAlign: 'start',
                  },
                  dir: 'ltr',
                }}
              />
              {isActive && (
                <CheckIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
    </SelfSetupTourProvider>
  );
};

export default Layout;
